// Markdown body for the post. Metadata lives in src/blog/posts.json.
const body = `
Hold the cameo up to a bright light. A shell cameo glows warm orange through the thin carved areas and you can often see fine growth lines running across it. Plastic and resin stay flat and dead, or glow evenly with no structure at all. That one test settles most cameos in a few seconds.

## What a cameo actually is

A cameo is carved in relief, standing up from its background. Its opposite, an intaglio, is carved into the surface and was historically used as a seal.

The trick that makes a cameo work is layering. The carver cuts through a pale outer layer to reveal a darker layer beneath, so the figure and the background are different colours in the same piece of material. That is why the material matters so much.

## The materials, and how to tell them apart

**Shell.** The most common antique cameo material, carved from helmet or queen conch shell. Warm cream figure on an orange, brown or pinkish ground. Translucent when backlit, with faint parallel growth lines. Slightly curved on the back, following the shell.

**Hardstone.** Agate, onyx, sardonyx. Sharp white on black, grey or brown. Cold to the touch, heavy for its size, and will not scratch with a pin. These are usually the most valuable.

**Lava.** Carved from Vesuvian lava, in soft greys, browns and greens, often deeply undercut and very detailed. Nineteenth century Grand Tour souvenirs.

**Coral.** Pink to salmon, carved in the round more often than in flat relief.

**Jet, bog oak, vulcanite.** Black. Jet is carved, surprisingly light and warm to the touch; vulcanite is moulded rather than carved and smells faintly of rubber when rubbed hard.

**Glass or paste.** Moulded, not carved. Look for a seam at the edge, tiny bubbles inside, and identical repeats.

**Plastic, resin, celluloid.** Modern reproductions. Light, warm, seamed, and often too perfect.

## The tests

| Test | Shell or hardstone | Moulded or plastic |
| --- | --- | --- |
| Backlight | Shell glows warm, shows growth lines; hardstone stays opaque | Flat, even glow or none at all |
| Temperature | Hardstone is cold; shell is cool briefly | Warm immediately |
| Weight | Hardstone is heavy; shell is light but firm | Very light |
| Under a loupe | Tool marks, tiny asymmetries, undercutting | Seams, bubbles, soft rounded detail |
| The back | Shell is slightly concave and unpolished | Flat, moulded, sometimes with a mould number |
| Tap on a tooth | Hard click | Dull plastic tap |

Never use a hot pin, whatever the internet says. It damages the piece and the smell test is not worth the burn mark.

The most telling sign of a hand carved cameo is **asymmetry**. A carver working by hand leaves the two sides of a face slightly different. Moulded pieces are perfectly symmetrical and repeat exactly from one example to the next.

## Dating a cameo

**The setting is better evidence than the carving.** Cameos were reset constantly, so a Georgian cameo can sit in a 1970s mount. Judge the mount on its own terms using our [era by era guide](/blog/how-to-date-antique-jewelry-by-era), then judge the cameo separately.

**Subject matter helps.** Classical goddesses and mythological scenes dominate the Georgian and early Victorian period. Idealised female profiles, often unnamed, run right through the nineteenth century. Rebecca at the Well, a woman before a small building, is a very common Victorian subject. Flowers and full figures appear later.

**Style of carving.** Early work tends to be deeply undercut with crisp detail. Later mass-market shell cameos are shallower and softer.

**Findings.** A brooch with a C clasp and a long pin extending past the frame points to the nineteenth century. A modern safety catch means the mount is later, or has been repaired.

**Habillé** cameos, where the figure wears real gemstones set into the carving, are a specific Victorian and Edwardian taste and usually indicate a better piece.

## What makes one worth more than another

In roughly this order:

1. **Material.** Hardstone above shell, generally, and both far above moulded imitations.
2. **Quality of carving.** Look at the hands, the hair and the drapery. Poor cameos have crude hands and blobby hair. Fine ones have individually carved strands and delicate fingers.
3. **Condition.** Cracks are the killer, particularly on shell. Backlight it and look for a line running through.
4. **The mount.** Gold rather than gilt metal, and original rather than replaced.
5. **Signature.** Signed cameos by known carvers are a specialist market of their own.
6. **Subject.** An unusual or identifiable subject beats another anonymous profile.

## Condition problems to look for

- **Cracks.** Hold it to the light. A crack across a shell cameo halves its value at least, and cannot be repaired invisibly.
- **Chips at the edge**, often hidden under the bezel. Ask for a photograph of the piece out of, or at least at an angle to, the mount.
- **Loss of detail** from over-cleaning or polishing.
- **Dryness.** Shell can dry and craze over decades. A very occasional wipe with a barely oily cloth is the traditional remedy, but do not soak a cameo, and never put one in an ultrasonic cleaner. Our [cleaning guide](/blog/how-to-clean-antique-jewelry) explains why organic materials and ultrasonics do not mix.
- **Replaced mounts.** Not a fault, but it should be reflected in the description.

## Buying one second hand

Ask for three photographs: the front in daylight, the back of the mount, and the cameo backlit. That third one answers the material question and shows any crack, and a seller who understands cameos will not find the request strange.

Ask whether the mount is original, what the metal is, and whether there is a hallmark. [Gold marks](/blog/how-to-tell-if-gold-jewelry-is-real) are worth knowing before you agree a price.

On Nextmine the buyer's payment is held until the piece has arrived and been confirmed, so a cameo that turns out to be resin rather than shell is a recoverable situation. [See what is listed](/s), or read [how payments work](/how-payments-work).
`;

export default body;
